"use client"

import React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Loader2, Plus } from "lucide-react"
import { useExplorerContext } from "@/components/explorer/explorer-context"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export function ReferenceDataView({ onAddValueMapping }: { onAddValueMapping: () => void }) {
  const { reference } = useExplorerContext()
  const { t, n } = useTranslation()
  if (!reference.selectedType) return null

  const showing = reference.filteredItems.length
  const shown =
    reference.search && !reference.searchResults
      ? t("explorer.referenceView.ofTotal", { count: n(showing), total: n(reference.items.length) })
      : n(showing)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <h3 className="font-display text-base font-bold">{reference.selectedType}</h3>
        <Input
          className="h-9 w-64 rounded-[9px] border-control-border text-[13px]"
          placeholder={t((reference.isClamped ? "explorer.referenceView.searchClamped" : "explorer.referenceView.filterValues"))}
          value={reference.search}
          onChange={(e) => reference.setSearch(e.target.value)}
        />
        <Button variant="outline" size="sm" className="h-9 rounded-[9px]" onClick={onAddValueMapping}>
          <Plus className="mr-1 h-4 w-4" /> {t("explorer.valueSet.title")}
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          {t((showing === 1 ? "explorer.referenceView.valuesOne" : "explorer.referenceView.valuesOther"), { shown })}
          {reference.isClamped && !reference.search ? ` · ${t("explorer.referenceView.clampedNote")}` : ""}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {reference.isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t("explorer.referenceView.loading")}
          </div>
        ) : reference.filteredItems.length > 0 ? (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card">
              <TableRow className="hover:bg-transparent">
                <TableHead className="th-section-label h-9 w-40">{t("explorer.referenceView.columns.id")}</TableHead>
                <TableHead className="th-section-label h-9">{t("explorer.referenceView.columns.name")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reference.filteredItems.map((item, index) => (
                <TableRow key={`${item.id}-${index}`} className="border-card-border hover:bg-row-hover">
                  <TableCell className="py-2 font-mono text-xs">{item.id}</TableCell>
                  <TableCell className="py-2 text-xs">{item.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">{t("explorer.common.noValuesFound")}</div>
        )}
      </div>
    </div>
  )
}
